/* Glue between a series definition, the live TutCtx, and the overlay UI.
 *
 * Responsibilities:
 *  - resolve the current step (wrong-tab steps become an arrow-toast pointing
 *    at the right tab button);
 *  - auto-advance a step when its `done` predicate flips false→true while it
 *    is current (already-true at entry shows "✓ already done" instead of
 *    silently skipping, so the teaching beat still lands);
 *  - keep the mission-panel checklist sticky as task predicates satisfy;
 *  - resolve {maxRounds}/{refPrice}/{className} copy tokens, and call the
 *    function form of a step body / task label with the live ctx. */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTutorial } from './useTutorial';
import { Spotlight, type Rect } from './Spotlight';
import { MissionPanel } from './MissionPanel';
import type { TutCtx, TutSeries } from './types';
import '../styles/tutorial.css';

function safe<T>(fn: () => T, fallback: T): T { try { return fn(); } catch { return fallback; } }

const resolveTokens = (body: string, ctx: TutCtx) =>
  body
    .replace(/\{maxRounds\}/g, String(ctx.maxRounds))
    .replace(/\{refPrice\}/g, ctx.refPrice)
    .replace(/\{className\}/g, ctx.className ?? 'this class');

/** Copy is either a literal or a function of the live ctx. */
const copy = (v: string | ((c: TutCtx) => string), ctx: TutCtx): string =>
  (typeof v === 'function' ? safe(() => v(ctx), '') : v);

export function TutorialHost({ series, ctx }: { series: TutSeries; ctx: TutCtx }) {
  const chapterIdx = useTutorial((z) => z.chapter);
  const stepIdx = useTutorial((z) => z.step);
  const finished = useTutorial((z) => z.finished);
  const achieved = useTutorial((z) => z.achieved);
  const panelCollapsed = useTutorial((z) => z.panelCollapsed);
  const next = useTutorial((z) => z.next);
  const back = useTutorial((z) => z.back);
  const dismiss = useTutorial((z) => z.dismiss);
  const stop = useTutorial((z) => z.stop);
  const markAchieved = useTutorial((z) => z.markAchieved);
  const setPanelCollapsed = useTutorial((z) => z.setPanelCollapsed);

  const ci = Math.min(chapterIdx, series.chapters.length - 1);
  const chapter = series.chapters[ci]!;
  const si = Math.min(stepIdx, chapter.steps.length - 1);
  const step = chapter.steps[si]!;

  const doneNow = step.done ? safe(() => !!step.done!(ctx), false) : false;

  // Auto-advance only on an observed false→true transition of the predicate.
  const key = `${series.id}:${ci}:${si}`;
  const baseline = useRef<{ key: string; val: boolean }>({ key: '', val: false });
  useEffect(() => {
    if (finished) return;
    if (baseline.current.key !== key) {
      baseline.current = { key, val: doneNow };
      return;
    }
    if (doneNow && !baseline.current.val) {
      baseline.current = { key, val: true };
      next();
    }
  });

  // Checklist tolerance for wandering: any task predicate that satisfies —
  // regardless of which step is current — checks off permanently.
  useEffect(() => {
    for (const ch of series.chapters) {
      for (const t of ch.tasks) {
        if (t.when && !safe(() => t.when!(ctx), false)) continue;
        if (!achieved[t.id] && safe(() => t.done(ctx), false)) markAchieved(t.id);
      }
    }
  });

  // Wrong tab → point at the tab button with a "get back on track" hint,
  // unless the step's own anchor IS that tab button (the chapter openers).
  const wrongTab = !!step.screen && ctx.tab !== step.screen;
  const tabAnchor = step.screen ? series.tabAnchor(step.screen) : undefined;
  const arrowMode = wrongTab && step.anchor !== tabAnchor;
  const anchor = arrowMode ? tabAnchor : step.anchor;
  const body = resolveTokens(arrowMode ? series.tabHint(step.screen!) : copy(step.body, ctx), ctx);
  const title = arrowMode ? undefined : step.title;

  const multiChapter = series.chapters.length > 1;
  const metaLabel = multiChapter
    ? `Chapter ${ci + 1}/${series.chapters.length} · Step ${si + 1}/${chapter.steps.length}`
    : `Step ${si + 1} of ${chapter.steps.length}`;
  const isLast = ci === series.chapters.length - 1 && si === chapter.steps.length - 1;

  // A task that cannot apply yet (no students to drill into) is left off the
  // checklist entirely rather than shown as a box the instructor can't tick.
  const tasksView = chapter.tasks
    .filter((t) => !t.when || safe(() => t.when!(ctx), false))
    .map((t) => ({
      id: t.id,
      label: resolveTokens(copy(t.label, ctx), ctx),
      done: !!achieved[t.id] || safe(() => t.done(ctx), false),
    }));
  const showPanel = finished ? !!series.finale : tasksView.length > 0;
  // The spotlight reports where its hole is so the mission panel can move
  // aside rather than cover the control the step is pointing at.
  const [holeRect, setHoleRect] = useState<Rect | null>(null);

  // Portalled to <body>. The overlay is position:fixed, and a fixed child of a
  // Tailwind `space-y-*` container inherits its `margin-top` — which offsets a
  // fixed box by exactly that much, so the spotlight hole used to land 20px
  // below its anchor on the game screen. A body portal also keeps the z-index
  // contract above independent of whatever stacking context the page builds.
  return createPortal(
    <>
      {!finished && (
        <Spotlight
          anchor={anchor}
          title={title}
          body={body}
          metaLabel={metaLabel}
          preDone={doneNow}
          canBack={ci > 0 || si > 0}
          isLast={isLast}
          onNext={next}
          onBack={back}
          onSkip={dismiss}
          onRect={setHoleRect}
        />
      )}
      {showPanel && (
        <MissionPanel
          seriesTitle={series.title}
          chapterIdx={ci}
          chapterCount={series.chapters.length}
          chapterTitle={chapter.title}
          tasks={tasksView}
          collapsed={panelCollapsed}
          finished={finished}
          finale={series.finale}
          onToggle={() => setPanelCollapsed(!panelCollapsed)}
          onSkip={dismiss}
          onCloseFinale={stop}
          avoid={finished ? null : holeRect}
        />
      )}
    </>,
    document.body,
  );
}
